import React, { useState, useEffect, useCallback } from 'react'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import { Link, useHistory } from 'react-router-dom'
import AuthenticationService from '../general/AuthenticationService.js'

/* ─── helpers ─── */
const fmt = n =>
    n == null || isNaN(n) ? null
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)

function expand(dict, msg) {
    const map = new Map(Object.entries(dict))
    let out = ''
    for (let i = 0; i < msg.length; i++) {
        out += msg[i] === '^' ? (map.get(msg[++i]) ?? '') : msg[i]
    }
    return out
}

/* ─── design tokens ─── */
const T = {
    bg: '#f1f5f9', card: '#ffffff', border: '#e8edf5',
    blue: '#3b82f6', blueD: '#2563eb', navy: '#0f172a',
    txt: '#1e293b', txt2: '#64748b', muted: '#94a3b8',
    green: '#16a34a', greenBg: '#f0fdf4', greenBdr: '#bbf7d0',
    red: '#dc2626', redBg: '#fef2f2', redBdr: '#fecaca',
    warn: '#f59e0b', warnBg: '#fffbeb', warnBdr: '#fde68a',
    r: 16, rsm: 10,
    shadow: '0 1px 3px rgba(15,23,42,.06),0 1px 2px rgba(15,23,42,.04)',
    shadowMd: '0 4px 14px rgba(15,23,42,.09)',
    font: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
}

/* ─── sub-components ─── */
function LoadingOverlay({ visible }) {
    if (!visible) return null
    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(241,245,249,.75)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
            <div style={{ background:T.card, borderRadius:T.r, padding:'32px 48px', boxShadow:T.shadowMd, display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
                <div className="zsp" />
                <span style={{ fontSize:14, color:T.txt2, fontWeight:500 }}>Procesando...</span>
            </div>
        </div>
    )
}

function SuccessBanner({ message }) {
    if (!message) return null
    return (
        <div style={{ background:T.greenBg, border:`1px solid ${T.greenBdr}`, borderRadius:T.rsm, padding:'12px 16px', display:'flex', alignItems:'center', gap:8, marginBottom:20, fontSize:13.5, color:'#065f46', fontWeight:500 }}>
            <span>✓</span>{message}
        </div>
    )
}

function ActionButton({ label, icon, onClick, primary, disabled }) {
    const [hov, setHov] = useState(false)
    return (
        <button onClick={onClick} disabled={disabled}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
                background: primary ? (hov ? T.blueD : T.blue) : (hov ? '#f8fafc' : T.card),
                border: `1px solid ${primary ? (hov ? T.blueD : T.blue) : T.border}`,
                borderRadius:T.rsm, color: primary ? '#fff' : T.txt,
                fontSize:13.5, fontWeight:550, fontFamily:T.font, cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1, transition:'all .15s', whiteSpace:'nowrap',
                boxShadow: primary && hov ? '0 4px 12px rgba(59,130,246,.35)' : T.shadow,
            }}>
            {icon && <span style={{ fontSize:16, lineHeight:1 }}>{icon}</span>}{label}
        </button>
    )
}

function Section({ title, subtitle, icon, children, style }) {
    return (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, padding:'20px 24px', boxShadow:T.shadow, ...style }}>
            {(title || subtitle) && (
                <div style={{ marginBottom:18 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        {icon && <span style={{ width:34, height:34, borderRadius:9, background:`${T.blue}12`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>{icon}</span>}
                        <div>
                            {title && <h2 style={{ margin:0, fontSize:16, fontWeight:650, color:T.txt, letterSpacing:'-0.01em' }}>{title}</h2>}
                            {subtitle && <p style={{ margin:'2px 0 0', fontSize:13, color:T.txt2 }}>{subtitle}</p>}
                        </div>
                    </div>
                </div>
            )}
            {children}
        </div>
    )
}

function AlertBadge({ count }) {
    if (!count) return null
    return (
        <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', minWidth:22, height:22, borderRadius:11, background:T.redBg, color:T.red, fontSize:12, fontWeight:650, padding:'0 6px', border:`1px solid ${T.redBdr}` }}>
            {count}
        </span>
    )
}

const PAGE_SIZE_CFDI = 25

function getPages(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
    if (cur <= 4)   return [1, 2, 3, 4, 5, '…', total]
    if (cur >= total - 3) return [1, '…', total-4, total-3, total-2, total-1, total]
    return [1, '…', cur-1, cur, cur+1, '…', total]
}
function PageBtn({ label, active, onClick }) {
    const [hov, setHov] = useState(false)
    return (
        <button onClick={onClick}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{ minWidth:30, height:30, padding:'0 7px', border:`1px solid ${active ? T.blue : T.border}`, borderRadius:7, background: active ? T.blue : (hov ? '#f0f7ff' : 'white'), color: active ? 'white' : (hov ? T.blue : T.txt2), fontSize:12.5, fontWeight: active ? 700 : 500, cursor:'pointer', fontFamily:T.font, transition:'all .12s' }}>
            {label}
        </button>
    )
}

/* ─── AlertaTable: colapsada por default, búsqueda + paginación ─── */
function AlertaTable({ listado, allSociedades, allClientes }) {
    const [collapsed, setCollapsed] = useState(true)   // ← cerrada por default
    const [search, setSearch]       = useState('')
    const [page, setPage]           = useState(1)

    if (!listado.alertas?.length) return null

    const isSOC      = listado.objectType === 'sociedad'
    const url        = isSOC ? 'sociedades' : 'personas-fisicas'
    const nameHeader = isSOC ? 'Razón Social' : 'Persona'

    // filtrar por búsqueda
    const q = search.toLowerCase().trim()
    const filtered = listado.alertas.filter(alerta => {
        if (!q) return true
        const msg = listado.mensajes ? expand(listado.mensajes, alerta.mensaje) : alerta.mensaje
        return (alerta.name || '').toLowerCase().includes(q) || (msg || '').toLowerCase().includes(q)
    })

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE_CFDI))
    const safePage   = Math.min(page, totalPages)
    const pageData   = filtered.slice((safePage - 1) * PAGE_SIZE_CFDI, safePage * PAGE_SIZE_CFDI)

    const handleSearch = v => { setSearch(v); setPage(1) }

    return (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, overflow:'hidden', boxShadow:T.shadow }}>
            {/* header colapsable */}
            <div onClick={() => setCollapsed(!collapsed)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', cursor:'pointer', borderBottom: collapsed ? 'none' : `1px solid ${T.border}`, background:'#fafbfd', transition:'background .15s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ width:30, height:30, borderRadius:8, background:T.warnBg, border:`1px solid ${T.warnBdr}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>⚠</span>
                    <span style={{ fontSize:14.5, fontWeight:600, color:T.txt, letterSpacing:'-0.01em' }}>{listado.encabezado}</span>
                    <AlertBadge count={listado.alertas.length} />
                    {q && filtered.length !== listado.alertas.length && (
                        <span style={{ fontSize:11, color:T.blue, fontWeight:600 }}>({filtered.length} filtradas)</span>
                    )}
                </div>
                <span style={{ fontSize:11, color:T.muted, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition:'transform .2s ease', userSelect:'none' }}>▼</span>
            </div>

            {!collapsed && (
                <>
                    {/* barra de búsqueda */}
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`, background:'#fdfeff' }}>
                        <div style={{ position:'relative', maxWidth:380 }}>
                            <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', color:T.muted, fontSize:14 }}>🔍</span>
                            <input
                                value={search}
                                onChange={e => handleSearch(e.target.value)}
                                placeholder="Buscar por nombre o alerta..."
                                style={{ width:'100%', padding:'8px 12px 8px 34px', border:`1px solid ${T.border}`, borderRadius:9, fontSize:13, color:T.txt, outline:'none', fontFamily:T.font, background:'white', boxSizing:'border-box' }}
                                onFocus={e  => e.target.style.borderColor = T.blue}
                                onBlur={e   => e.target.style.borderColor = T.border}
                            />
                            {search && (
                                <button onClick={() => handleSearch('')} style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:T.muted, fontSize:14, lineHeight:1, padding:0 }}>✕</button>
                            )}
                        </div>
                    </div>

                    {/* tabla */}
                    <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13.5, fontFamily:T.font }}>
                            <thead>
                                <tr>
                                    {['#', nameHeader, 'Alerta', ...(isSOC ? ['Cliente · Propietario'] : [])].map((h, i) => (
                                        <th key={i} style={{ textAlign:'left', padding:'9px 16px', fontSize:11, fontWeight:600, color:T.muted, textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:`1px solid ${T.border}`, background:'#fafbfd', whiteSpace:'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageData.length === 0 ? (
                                    <tr><td colSpan={isSOC ? 4 : 3} style={{ padding:'36px 16px', textAlign:'center', color:T.muted, fontSize:13 }}>Sin resultados para "{search}"</td></tr>
                                ) : pageData.map((alerta, idx) => {
                                    const msg   = listado.mensajes ? expand(listado.mensajes, alerta.mensaje) : alerta.mensaje
                                    const soc   = isSOC ? allSociedades.find(x => x.id === alerta.objectId) : null
                                    const monto = fmt(alerta.cantidad)
                                    const isNeg = alerta.cantidad < 0
                                    const globalIdx = (safePage - 1) * PAGE_SIZE_CFDI + idx + 1

                                    return (
                                        <tr key={`${alerta.objectId}-${idx}`}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            style={{ borderBottom:`1px solid ${T.border}`, transition:'background .1s' }}>
                                            <td style={{ padding:'11px 16px', color:T.muted, fontWeight:500, width:44 }}>{globalIdx}</td>
                                            <td style={{ padding:'11px 16px' }}>
                                                <Link to={`/${url}/reportes/${alerta.objectId}`}
                                                    style={{ color:T.blue, textDecoration:'none', fontWeight:550 }}>
                                                    {alerta.name}
                                                </Link>
                                            </td>
                                            <td style={{ padding:'11px 16px' }}>
                                                <p style={{ margin:'0 0 3px', color:T.txt, fontSize:13, lineHeight:1.5 }}>{msg}</p>
                                                {monto && (
                                                    <span style={{ fontSize:13, fontWeight:700, fontFamily:'monospace', color: isNeg ? T.red : T.green }}>{monto}</span>
                                                )}
                                            </td>
                                            {isSOC && (
                                                <td style={{ padding:'11px 16px' }}>
                                                    {soc?.clientes?.map((cm, ci) => {
                                                        const c = allClientes.find(x => x.id === cm.id)
                                                        return c ? (
                                                            <span key={ci} style={{ display:'inline-block', background:'#f1f5f9', borderRadius:6, padding:'3px 8px', fontSize:12, color:T.txt2, fontWeight:500, marginRight:4, marginBottom:4 }}>
                                                                {c.nombreCliente} · {c.propietario}
                                                            </span>
                                                        ) : null
                                                    })}
                                                </td>
                                            )}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* paginación */}
                    {totalPages > 1 && (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', borderTop:`1px solid ${T.border}`, flexWrap:'wrap', gap:8 }}>
                            <span style={{ fontSize:12, color:T.muted }}>
                                Mostrando {(safePage-1)*PAGE_SIZE_CFDI+1}–{Math.min(safePage*PAGE_SIZE_CFDI, filtered.length)} de {filtered.length}
                            </span>
                            <div style={{ display:'flex', gap:4 }}>
                                {getPages(safePage, totalPages).map((p, i) =>
                                    p === '…'
                                        ? <span key={i} style={{ padding:'0 4px', color:T.muted, alignSelf:'center' }}>…</span>
                                        : <PageBtn key={p} label={p} active={p === safePage} onClick={() => setPage(p)} />
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

/* ═══════════════════ MAIN ═══════════════════ */
function CfdiComponent(props) {
    const history = useHistory()
    const [refreshing, setRefreshing] = useState(false)
    const [refreshMsg, setRefreshMsg]   = useState(null) // {text, ok}
    const [elements, setElements]     = useState([])
    const [allSociedades, setSocs]    = useState([])
    const [allClientes, setClis]      = useState([])
    const [message]                   = useState(props.message || '')

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CFDIS')) {
            alert('Usuario no autorizado')
            history.push('/login')
            return
        }
        fetchData()
    }, []) // eslint-disable-line

    const fetchData = useCallback(async () => {
        try {
            const socR = await ElementsDataService.retrieveAllElements('sociedades')
            setSocs(socR.data.map(s => ({ id:s.id, razonSocial:s.razonSocial, rfc:s.rfc, clientes:s.clientes })))
            const cliR = await ElementsDataService.retrieveAllElements('clientes')
            setClis(cliR.data)
            if (AuthenticationService.haveAuthorization('READ_CONTABILIDAD')) {
                const altR = await ElementsDataService.retrieveAllElements('alertas-contables')
                setElements(altR.data)
            }
        } catch (err) { console.error('CFDI fetch:', err) }
    }, [])

    const goRefreshStatus = async () => {
        setRefreshing(true)
        setRefreshMsg(null)
        try {
            const r = await ElementsDataService.retrieveAllElements('generar-status-contable')
            const msg = r.data?.message || 'Proceso completado'
            const ok = !msg.toLowerCase().includes('desactivado') && !msg.toLowerCase().includes('error')
            setRefreshMsg({ text: msg, ok })
            setTimeout(() => setRefreshMsg(null), 6000)
        } catch(e) {
            setRefreshMsg({ text: 'Error al actualizar el status contable.', ok: false })
            setTimeout(() => setRefreshMsg(null), 6000)
        }
        finally { setRefreshing(false) }
    }

    if (!AuthenticationService.isUserLoggedIn()) return null

    const canWrite  = AuthenticationService.haveAuthorization('WRITE_CFDIS')
    const canContab = AuthenticationService.haveAuthorization('READ_CONTABILIDAD')
    const totalAlertas = elements.reduce((s, l) => s + (l.alertas?.length || 0), 0)

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'32px 32px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes zsp { to { transform:rotate(360deg); } }
                .zsp { width:32px; height:32px; border:3px solid ${T.border}; border-top-color:${T.blue}; border-radius:50%; animation:zsp .7s linear infinite; }
            `}</style>

            <LoadingOverlay visible={refreshing} />

            {refreshMsg && (
                <div style={{
                    margin: '0 32px 0', padding: '12px 20px', borderRadius: 12,
                    background: refreshMsg.ok ? '#f0fdf4' : '#fff7ed',
                    border: `1px solid ${refreshMsg.ok ? '#bbf7d0' : '#fed7aa'}`,
                    color: refreshMsg.ok ? '#16a34a' : '#c2410c',
                    fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8
                }}>
                    {refreshMsg.ok ? '✅' : '⚠️'} {refreshMsg.text}
                </div>
            )}

            <div style={{ maxWidth:1120, margin:'0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom:26 }}>
                    <h1 style={{ margin:0, fontSize:26, fontWeight:700, color:T.navy, letterSpacing:'-0.02em' }}>CFDIs</h1>
                    <p style={{ margin:'4px 0 0', fontSize:14, color:T.txt2 }}>Gestión de comprobantes fiscales, declaraciones y alertas contables</p>
                </div>

                <SuccessBanner message={message} />

                {/* Carga */}
                {canWrite && (
                    <Section title="Carga de Datos" subtitle="Metadata, declaraciones y opiniones" icon="✏️" style={{ marginBottom:14 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                            <ActionButton label="Agregar Metadata"                icon="＋"  onClick={props.addElementClicked} primary />
                            <ActionButton label="Agregar Declaración Provisional" icon="📄" onClick={() => history.push('/declaraciones-provisionales/')} />
                            <ActionButton label="Agregar Opinión Cumplimiento"    icon="📋" onClick={() => history.push('/cargar-opiniones-cumplimiento/')} />
                        </div>
                    </Section>
                )}

                {/* Reportes */}
                {canContab && (
                    <Section title="Reportes y Consultas" subtitle="Opiniones, revisiones y relación de declaraciones" icon="📊" style={{ marginBottom:14 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                            <ActionButton label="Opiniones Cumplimiento"      icon="✓"  onClick={() => history.push('/opiniones-cumplimiento/')} />
                            <ActionButton label="Revisión Proyectos Mensual"  icon="🔍" onClick={() => history.push('/revision-plaza-mensual/')} />
                            <ActionButton label="Sociedades Revisadas"        icon="📑" onClick={() => history.push('/reporte-sociedades-revisadas/')} />
                            <ActionButton label="Relación Declaraciones"      icon="📝" onClick={() => history.push('/relacion-declaraciones/')} />
                            <ActionButton label="Obtener CFDIs"               icon="📥" onClick={() => history.push('/get-cfdis/')} />
                            <ActionButton label="Reporte Contable Clientes"   icon="📊" onClick={() => history.push('/clientes/reportes/-1')} />
                            <ActionButton
                                label={refreshing ? 'Actualizando...' : '↻ Actualizar Status Contable'}
                                icon="🔄"
                                onClick={goRefreshStatus}
                                disabled={refreshing}
                            />
                        </div>
                    </Section>
                )}

                {/* Alertas */}
                {canContab && elements.length > 0 && (
                    <div style={{ marginTop:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                            <h2 style={{ margin:0, fontSize:18, fontWeight:650, color:T.navy, letterSpacing:'-0.01em' }}>Alertas Contables</h2>
                            <AlertBadge count={totalAlertas} />
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                            {elements.map((listado, i) => (
                                <AlertaTable key={i} listado={listado} allSociedades={allSociedades} allClientes={allClientes} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty */}
                {canContab && elements.length === 0 && (
                    <Section style={{ marginTop:14, textAlign:'center', padding:'48px 24px' }}>
                        <div style={{ fontSize:36, marginBottom:10 }}>📋</div>
                        <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.txt }}>Sin alertas contables</p>
                        <p style={{ margin:'4px 0 0', fontSize:13, color:T.txt2 }}>Todas las sociedades están al corriente</p>
                    </Section>
                )}
            </div>
        </div>
    )
}

export default CfdiComponent
